import { observer, useLocalObservable } from 'mobx-react';
import { useEffect } from 'react';
import AgentSidebarMenuEntry, { AGENT_ICONS } from './AgentSidebarMenuEntry';
import AgentSideBarShowMoreButton from './AgentSideBarShowMoreButton';
import AgentSidebarCollapseButton from './AgentSidebarCollapseButton';
import AgentUsagePanel from './AgentUsagePanel';
import { ICON_BUTTON_ICONS } from '../../buttons/IconButton';
import { paths } from '../../../utils/constants/routes';

const AgentSidebar = observer(
  ({
    onNewChat = () => {},
    onSearchConversations = () => {},
    onBrowseByProject = () => {},
    onFavorites = () => {},
    onArchived = () => {},
    onShowMoreRecentChats = () => {},
    onCollapseChange = () => {},
    onRenameChat = () => {},
    onToggleChatFavorite = () => {},
    onArchiveChat = () => {},
    onDeleteChat = () => {},
    onChatClick = () => {},
    recentChats = [],
    isLoadingRecentChats = false,
    activeRoute = ''
  }) => {
    const state = useLocalObservable(() => ({
      // Manual collapse state (user toggle)
      isCollapsed: false,

      // Responsive collapse state (viewport size)
      isResponsiveCollapsed: false,

      // Computed effective state - collapsed if either manual OR responsive
      get effectivelyCollapsed() {
        return state.isCollapsed || state.isResponsiveCollapsed;
      },

      toggleCollapsed: () => {
        state.isCollapsed = !state.isCollapsed;
        onCollapseChange(state.isCollapsed);
      },

      setResponsiveCollapsed: (collapsed) => {
        state.isResponsiveCollapsed = collapsed;
      }
    }));

    // Responsive breakpoint detection - matches CSS @media (max-width: 768px)
    useEffect(() => {
      const RESPONSIVE_BREAKPOINT = 768;

      const checkViewportWidth = () => {
        const shouldBeResponsiveCollapsed = window.innerWidth <= RESPONSIVE_BREAKPOINT;
        state.setResponsiveCollapsed(shouldBeResponsiveCollapsed);
      };

      // Check initial viewport size
      checkViewportWidth();

      // Listen for resize events
      window.addEventListener('resize', checkViewportWidth);

      // Cleanup listener
      return () => {
        window.removeEventListener('resize', checkViewportWidth);
      };
    }, []);

    // Handle search conversations click - navigates to all conversations page
    const handleSearchConversations = () => {
      onSearchConversations(); // Call parent handler to navigate
    };

    // Note: handleBrowseByProject removed - button temporarily hidden (feature not implemented)

    // Handle context menu actions for recent chats
    const handleContextMenuAction = (action, chatId) => {
      switch (action) {
        // Note: 'rename' case removed - inline editing now handles renames
        case 'favorite':
          onToggleChatFavorite(chatId);
          break;
        case 'archive':
          onArchiveChat(chatId);
          break;
        case 'delete':
          onDeleteChat(chatId);
          break;
        default:
          console.warn('Unknown context menu action:', action);
      }
    };

    // Get context menu items for a chat
    const getContextMenuItems = (chat) => [
      {
        icon: ICON_BUTTON_ICONS.PEN,
        label: 'Rename',
        action: 'rename'
      },
      {
        icon: ICON_BUTTON_ICONS.BOOKMARK,
        label: chat.isFavorite ? 'Remove from Favorites' : 'Add to Favorites',
        action: 'favorite'
      },
      {
        icon: ICON_BUTTON_ICONS.ARCHIVE,
        label: chat.isArchived ? 'Unarchive' : 'Archive',
        action: 'archive'
      },
      {
        type: 'separator'
      },
      {
        icon: ICON_BUTTON_ICONS.TRASH,
        label: 'Delete',
        action: 'delete',
        className: 'delete-action'
      }
    ];

    return (
      <div className={`agent-sidebar ${state.effectivelyCollapsed ? 'collapsed' : ''}`}>
        {/* Only show collapse button when not in responsive mode */}
        {!state.isResponsiveCollapsed && (
          <AgentSidebarCollapseButton
            onClick={state.toggleCollapsed}
            tooltipText={state.isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            showTooltip={state.effectivelyCollapsed}
          />
        )}

        <div className="agent-sidebar-main-menu">
          <AgentSidebarMenuEntry
            label={state.effectivelyCollapsed ? '' : 'New chat'}
            icon={ICON_BUTTON_ICONS.NEW_CHAT}
            onClick={onNewChat}
            tooltipText="New chat"
            showTooltip={state.effectivelyCollapsed}
          />
          <AgentSidebarMenuEntry
            label={state.effectivelyCollapsed ? '' : 'Search conversations'}
            icon={ICON_BUTTON_ICONS.SEARCH}
            onClick={handleSearchConversations}
            tooltipText="Search conversations"
            showTooltip={state.effectivelyCollapsed}
          />
          {/* TODO: Browse by project feature not yet implemented - temporarily hidden */}
          {/* <AgentSidebarMenuEntry
            label={state.effectivelyCollapsed ? "" : "Browse by project"}
            icon={ICON_BUTTON_ICONS.PROJECT}
            onClick={handleBrowseByProject}
            tooltipText="Browse by project"
            showTooltip={state.effectivelyCollapsed}
          /> */}
          <AgentSidebarMenuEntry
            label={state.effectivelyCollapsed ? '' : 'Favorites'}
            icon={ICON_BUTTON_ICONS.BOOKMARK}
            onClick={onFavorites}
            tooltipText="Favorites"
            showTooltip={state.effectivelyCollapsed}
            isHighlighted={activeRoute.includes('/favorites')}
          />
          <AgentSidebarMenuEntry
            label={state.effectivelyCollapsed ? '' : 'Archived'}
            icon={ICON_BUTTON_ICONS.ARCHIVE}
            onClick={onArchived}
            tooltipText="Archived"
            showTooltip={state.effectivelyCollapsed}
            isHighlighted={activeRoute.includes('/archived')}
          />
        </div>

        {!state.effectivelyCollapsed && (recentChats.length > 0 || isLoadingRecentChats) && (
          <div className="agent-sidebar-recent-section">
            <div className="agent-sidebar-divider" />

            <h4 className="agent-sidebar-section-title">Recent chats</h4>

            {/* Only show loading indicator when we have NO cached data
                If we have cached chats, keep showing them while loading new data */}
            {isLoadingRecentChats && recentChats.length === 0 ? (
              <div className="agent-sidebar-loading">
                <div className="agent-sidebar-loading-text">Loading conversations...</div>
              </div>
            ) : (
              <>
                <div className="agent-sidebar-recent-list">
                  {recentChats.map((chat, index) => (
                    <AgentSidebarMenuEntry
                      key={chat.id}
                      label={chat.label}
                      icon={ICON_BUTTON_ICONS.RECENT_CHAT}
                      onClick={() => onChatClick(chat.id)}
                      withBorder={true}
                      isHighlighted={activeRoute.includes(`/chat/${chat.id}`)}
                      showContextMenu={true} // Show context menu for all recent chats
                      contextMenuItems={getContextMenuItems(chat)}
                      onContextMenuAction={(action) => handleContextMenuAction(action, chat.id)}
                      onRename={(newName) => onRenameChat(chat.id, newName)}
                      showTooltip={state.effectivelyCollapsed}
                      tooltipText={chat.label}
                      isFavorite={chat.isFavorite}
                      isArchived={chat.isArchived}
                      chatUrl={`${window.location.origin}${paths.AGENT}/chat/${chat.id}`}
                    />
                  ))}
                </div>

                <div className="agent-sidebar-show-more-wrap">
                  <AgentSideBarShowMoreButton
                    onClick={onShowMoreRecentChats}
                    isActive={activeRoute.includes('/conversations')}
                  />
                </div>
              </>
            )}
          </div>
        )}

        <AgentUsagePanel collapsed={state.effectivelyCollapsed} />
      </div>
    );
  }
);

export default AgentSidebar;
